import React, { useCallback, useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import { api } from "@/lib/api";
import { Preparing, isPreparing } from "@/components/Preparing";
import { useAuth } from "@/context/AuthContext";
import { X } from "lucide-react";

function IdList({ title, ids, onClose }) {
  return (
    <div className="fixed inset-0 z-[150] bg-black/40 flex items-center justify-center p-6" onClick={onClose} data-testid="lb-list-modal">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-5 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between"><h3 className="font-semibold text-[#333333]">{title}</h3><button onClick={onClose} data-testid="lb-list-close"><X className="w-4 h-4 text-[#6B7280]" /></button></div>
        {ids.length === 0 ? <div className="text-sm text-[#6B7280]">No cases.</div> : <div className="flex flex-wrap gap-1.5">{ids.map((id) => <span key={id} className="font-mono text-xs bg-[#F7F7F7] rounded px-2 py-1" data-testid={`lb-list-id-${id}`}>{id}</span>)}</div>}
        <div className="text-[11px] text-[#6B7280]">Case IDs only — no customer details on the leaderboard.</div>
      </div>
    </div>
  );
}

const AWARD_LABEL = (data, key) => (data?.awards || []).find((a) => a.key === key)?.label || key;
const METRICS = [["updates", "Updates", (m) => m.updates], ["stage_moves", "Stage moves", (m) => m.stage_moves],
  ["fu_due", "Follow-ups on time", (m) => `${m.fu_pct}%`], ["fresh", "Fresh conversations", (m) => `${m.fresh_pct}%`],
  ["airtable_edits", "Airtable edits", (m) => m.airtable_edits]];

function Podium({ rows, data }) {
  const top = rows.slice(0, 3);
  const order = [top[1], top[0], top[2]].filter(Boolean);
  const medal = { 1: "🥇", 2: "🥈", 3: "🥉" };
  return (
    <div className="flex items-end justify-center gap-4" data-testid="lb-podium">
      {order.map((r) => (
        <div key={r.rm} className={`rounded-2xl border border-[#E2E2E2] bg-white shadow-sm px-6 py-4 text-center ${r.rank === 1 ? "min-w-[220px] -translate-y-3" : "min-w-[180px]"} ${r.joined ? "" : "opacity-60"}`} data-testid={`lb-podium-${r.rank}`}>
          <div className="text-3xl">{medal[r.rank]}</div>
          <div className="font-semibold text-[#333333] mt-1">{r.name}</div>
          <div className="text-2xl font-bold text-[#FF4802]">{r.score}</div>
          <div className="flex flex-wrap justify-center gap-1 mt-2">{r.awards.map((a) => <span key={a} className="text-[11px] bg-[#F7F7F7] rounded-full px-2 py-0.5" title={AWARD_LABEL(data, a)}>{AWARD_LABEL(data, a)}</span>)}</div>
        </div>
      ))}
    </div>
  );
}

function Table({ data, onList }) {
  const rows = data.rows;
  return (
    <div className="rounded-2xl border border-[#E2E2E2] bg-white shadow-sm overflow-x-auto" data-testid="lb-table">
      <table className="w-full text-sm">
        <thead><tr className="text-left text-xs uppercase text-[#6B7280] border-b border-[#E2E2E2]">{["#", "RM", "Score", ...METRICS.map((m) => m[1]), "Awards"].map((h) => <th key={h} className="py-2 px-3">{h}{h === "Airtable edits" && data.airtable_edits_estimated ? <span className="ml-1 normal-case text-[#92600a]" title="Credited to the case's HL RM until Airtable has a Last-modified-by field">(estimated)</span> : null}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.rm} className={`border-b border-[#F7F7F7] ${r.joined ? "" : "text-[#9CA3AF]"}`} data-testid={`lb-row-${r.rm}`}>
              <td className="py-2 px-3 font-mono">{r.rank}</td>
              <td className="py-2 px-3 font-medium">{r.name} {r.joined ? <span className="text-[10px] bg-[#159A50]/10 text-[#159A50] rounded-full px-1.5 py-0.5 ml-1" data-testid={`lb-joined-${r.rm}`}>🏁 joined</span> : <span className="text-[10px] text-[#9CA3AF] ml-1" data-testid={`lb-notjoined-${r.rm}`}>Not at the start line yet</span>}{i === rows.length - 1 && rows.length > 1 && <span className="ml-2 text-[11px] text-[#6B7280]">Still warming up 🐢</span>}</td>
              <td className="py-2 px-3 font-bold text-[#FF4802]">{r.score}</td>
              {METRICS.map(([k, label, fmt]) => (
                <td key={k} className="py-2 px-3"><button onClick={() => onList(`${r.name} · ${label}`, r.lists[k === "fu_due" ? "fu_due" : k] || [])} className="underline decoration-dotted hover:text-[#FF4802]" data-testid={`lb-metric-${r.rm}-${k}`}>{fmt(r.metrics)}</button></td>
              ))}
              <td className="py-2 px-3 text-xs">{r.awards.map((a) => AWARD_LABEL(data, a)).join(" · ")}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={9} className="py-4 px-3 text-[#6B7280]">No HL RMs ranked yet.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}

function MyScore({ mine, data }) {
  if (!mine) return null;
  const p = mine.points;
  return (
    <div className="rounded-2xl border border-[#FF4802]/30 bg-[#FF4802]/5 p-5 space-y-2" data-testid="lb-my-score">
      <div className="flex items-baseline justify-between"><h3 className="font-semibold text-[#333333]">My score · #{mine.rank}</h3><div className="text-2xl font-bold text-[#FF4802]">{mine.score}</div></div>
      <div className="text-xs text-[#6B7280] grid grid-cols-2 sm:grid-cols-5 gap-2">
        <div>Updates {mine.metrics.updates} → <b>+{p.updates}</b></div><div>Stage moves {mine.metrics.stage_moves} → <b>+{p.stages}</b></div>
        <div>Follow-ups {mine.metrics.fu_pct}% → <b>+{p.fu}</b></div><div>Fresh {mine.metrics.fresh_pct}% → <b>+{p.fresh}</b></div>
        <div>Airtable edits {mine.metrics.airtable_edits} → <b className={p.airtable < 0 ? "text-[#CA1212]" : ""}>{p.airtable}</b></div>
      </div>
      {mine.quick_wins.length > 0 && <div className="text-sm" data-testid="lb-quick-wins"><div className="text-xs uppercase text-[#6B7280] mb-1">Quick wins</div><ul className="space-y-0.5">{mine.quick_wins.map((w, i) => <li key={i}>• {w.text} <span className="text-[#159A50] font-medium">{w.pts}</span>{w.ids.length > 0 && <span className="text-xs text-[#6B7280]"> ({w.ids.join(", ")})</span>}</li>)}</ul></div>}
      <div className="text-[11px] text-[#6B7280]">{data.formula}</div>
    </div>
  );
}

export default function Leaderboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const [list, setList] = useState(null);
  const [real, setReal] = useState(false);
  const isAdmin = (user?.roles || []).includes("Admin");
  const load = useCallback(async () => { try { setD((await api.get("/leaderboard", { params: real ? { real: 1 } : {} })).data); } catch { setD({ error: true }); } }, [real]);
  useEffect(() => { load(); }, [load]);
  if (!d) return <Layout><div className="p-8 text-sm text-[#6B7280]">Loading…</div></Layout>;
  if (isPreparing(d)) return <Layout><Preparing section="the Leaderboard" onRetry={load} /></Layout>;
  if (d.error) return <Layout><div className="p-8 text-sm text-[#CA1212]">Couldn't load the leaderboard.</div></Layout>;
  const tw = d.this_week;
  return (
    <Layout>
      <div className="max-w-6xl mx-auto px-6 py-8 space-y-6" data-testid="leaderboard-page">
        <div className="flex items-baseline justify-between flex-wrap gap-2">
          <h1 className="text-2xl font-bold text-[#333333]">🏆 Leaderboard</h1>
          <div className="flex items-center gap-3">
            {isAdmin && <label className="text-xs text-[#6B7280] flex items-center gap-1.5 cursor-pointer" data-testid="lb-real-toggle-label"><input type="checkbox" checked={real} onChange={(e) => { setD(null); setReal(e.target.checked); }} data-testid="lb-real-toggle" className="accent-[#FF4802]" /> Preview with real RMs</label>}
            <div className="text-xs text-[#6B7280]" data-testid="lb-week">Week {tw.week} → {tw.week_end} · Mon 00:00–Sun 23:59 IST · {tw.real_preview ? "computed now from Airtable (updates/stage moves need the live audit log)" : `updated ${new Date(tw._snapshot_at).toLocaleTimeString("en-IN")}`}</div>
          </div>
        </div>
        <Podium rows={tw.rows} data={tw} />
        {d.eligible && <MyScore mine={d.mine} data={tw} />}
        <Table data={tw} onList={(title, ids) => setList({ title, ids })} />
        {d.last_week && (
          <div className="space-y-2" data-testid="lb-last-week">
            <h2 className="text-base font-semibold text-[#333333]">Last week · {d.last_week.week} → {d.last_week.week_end} {d.last_week.frozen ? <span className="text-xs text-[#6B7280]">(final, frozen Monday 10:00 IST)</span> : <span className="text-xs text-[#92600a]">(provisional until Monday 10:00 IST)</span>}</h2>
            <Table data={d.last_week} onList={(title, ids) => setList({ title, ids })} />
          </div>
        )}
        <div className="text-xs text-[#6B7280]">Awards: {tw.awards.map((a) => `${a.label} — ${a.how}`).join(" · ")}</div>
        <div className="text-sm text-center text-[#6B7280] pt-2" data-testid="lb-footer">{tw.footer}</div>
        {!user && null}
      </div>
      {list && <IdList title={list.title} ids={list.ids} onClose={() => setList(null)} />}
    </Layout>
  );
}
